import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  BookOpen,
  Heart,
  Sun,
  Leaf,
  Sparkles,
  Star,
  Trash2,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { format, isToday, isYesterday } from "date-fns";

const MOODS = [
  { value: "grateful", label: "Grateful", icon: Heart, color: "mood-grateful" },
  { value: "happy", label: "Happy", icon: Sun, color: "mood-happy" },
  { value: "peaceful", label: "Peaceful", icon: Leaf, color: "mood-peaceful" },
  { value: "excited", label: "Excited", icon: Sparkles, color: "mood-excited" },
  { value: "loved", label: "Loved", icon: Star, color: "mood-loved" },
];

export default function Gratitude() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEntry, setNewEntry] = useState("");
  const [selectedMood, setSelectedMood] = useState("grateful");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/gratitude`);
      setEntries(response.data);
    } catch (error) {
      toast.error("Failed to load journal");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newEntry.trim()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/gratitude`, {
        content: newEntry,
        mood: selectedMood,
      });
      setEntries((prev) => [response.data, ...prev]);
      setNewEntry("");
      setSelectedMood("grateful");
      toast.success("Entry added! Keep spreading positivity! 🌟");
    } catch (error) {
      toast.error("Failed to add entry");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await axios.delete(`${API}/gratitude/${entryId}`);
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
      toast.success("Entry removed");
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) return "Today";
    if (isYesterday(date)) return "Yesterday";
    return format(date, "MMMM d, yyyy");
  };

  const getMoodIcon = (mood) => {
    const moodData = MOODS.find((m) => m.value === mood);
    return moodData ? moodData.icon : Heart;
  };

  const getMoodColor = (mood) => {
    const moodData = MOODS.find((m) => m.value === mood);
    return moodData ? moodData.color : "mood-grateful";
  };

  // Group entries by date
  const groupedEntries = entries.reduce((acc, entry) => {
    const date = formatDate(entry.created_at);
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div
        className="relative overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(253, 252, 248, 0.9), rgba(253, 252, 248, 1)), url('https://images.unsplash.com/photo-1763102917373-8066f6bf08a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')`,
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-secondary" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Gratitude Journal
            </h1>
            <p className="text-muted-foreground text-lg max-w-md mx-auto">
              Take a moment each day to reflect on what makes you happy
            </p>
          </div>

          {/* New entry form */}
          <form onSubmit={handleSubmit} className="card-happy">
            <div className="space-y-4">
              <div className="relative">
                <Textarea
                  data-testid="gratitude-input"
                  placeholder="What are you grateful for today?"
                  value={newEntry}
                  onChange={(e) => setNewEntry(e.target.value)}
                  className="input-happy min-h-[120px] font-handwriting text-xl resize-none"
                />
              </div>

              {/* Mood selector */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground mr-2">
                  How are you feeling?
                </span>
                {MOODS.map((mood) => {
                  const Icon = mood.icon;
                  return (
                    <button
                      key={mood.value}
                      type="button"
                      data-testid={`mood-btn-${mood.value}`}
                      onClick={() => setSelectedMood(mood.value)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-all ${
                        selectedMood === mood.value
                          ? mood.color + " border-2 scale-105"
                          : "bg-muted hover:bg-muted/80"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {mood.label}
                    </button>
                  );
                })}
              </div>

              <Button
                type="submit"
                data-testid="add-gratitude-btn"
                disabled={submitting || !newEntry.trim()}
                className="w-full btn-primary"
              >
                <Plus className="w-5 h-5 mr-2" />
                {submitting ? "Adding..." : "Add to Journal"}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Entries */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-xl font-semibold mb-6">Your Reflections</h2>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card-happy animate-pulse h-24"></div>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="card-happy text-center py-12">
            <Leaf className="w-12 h-12 text-secondary mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Your journal is empty</h3>
            <p className="text-muted-foreground">
              Start writing what you're grateful for today
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[600px] pr-4">
            <div className="space-y-8" data-testid="gratitude-entries">
              {Object.entries(groupedEntries).map(([date, dateEntries]) => (
                <div key={date}>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-4 sticky top-0 bg-background py-2">
                    {date}
                  </h3>
                  <div className="space-y-4">
                    {dateEntries.map((entry) => {
                      const MoodIcon = getMoodIcon(entry.mood);
                      return (
                        <div
                          key={entry.id}
                          className="card-happy group relative"
                          data-testid={`gratitude-entry-${entry.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-3">
                                <Badge
                                  className={`${getMoodColor(entry.mood)} flex items-center gap-1`}
                                >
                                  <MoodIcon className="w-3 h-3" />
                                  {MOODS.find((m) => m.value === entry.mood)?.label}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(entry.created_at), "h:mm a")}
                                </span>
                              </div>
                              <p className="font-handwriting text-xl text-foreground leading-relaxed">
                                {entry.content}
                              </p>
                            </div>
                            <button
                              data-testid={`delete-gratitude-btn-${entry.id}`}
                              onClick={() => handleDelete(entry.id)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-destructive/10 rounded-full text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </main>

      <Navigation />
    </div>
  );
}
